<template>
    <div class="firmware-upgrade new_background-main" v-if="isShowUpgradeFirmware">
        <div class="mgx_top_right_fixed" v-if="!isUpgrading">
            <div class="top_right_btn mgx_action_btn ghost" @click="back">
                <span class="btn_text">{{ $t('back') }}</span>
            </div>
        </div>
        <div class="upgrade-view">
            <!-- 选择.bin 文件 -->
            <template v-if="!isUpgrading">
                <div class="select-file">
                    <div class="file-name">{{ filePath }}</div>
                    <div class="select-file-btn">
                        <div class="mgx_action_btn ghost" :class="{ 'active': filePath.length > 0 }">
                            <svg class="btn_icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M17 8L12 3M12 3L7 8M12 3V15" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <span class="btn_text">{{
                                filePath.length > 0
                                    ?
                                    $t('upgrade_firmware_change') : $t('upgrade_firmware_select') }}</span>
                        </div>
                        <input class="file-input" type="file" @change="handleFileUpload" />
                    </div>
                </div>

                <div class="upgrade-message">{{ $t('upgrade_firmware_select_file_message') }}
                </div>

                <!-- 开始升级 -->
                <div class="start-upgrade" v-if="filePath.length > 0">
                    <div class="start_upgrade_text">{{ $t("start_upgrade") }}</div>
                    <div class="mgx_action_btn ghost" @click="handleUpgrade">
                        <svg class="btn_icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor"/>
                        </svg>
                        <span class="btn_text">{{ $t("start_upgrade_btn") }}</span>
                    </div>
                </div>
            </template>

            <div class="upgrade-progress" v-if="isUpgrading">
                <div class="progress-text-container">
                    <img class="icon" src="@/assets/img/icon_program_upgrade.svg"></img>
                    <div class="progress-text" v-if="upgradeStatus <= FirmwareUpgradeStatus.AppToBoot">{{
                        $t('firmware_upgrade_enter_upgrade_mode') }}</div>
                    <div class="progress-text" v-if="upgradeStatus == FirmwareUpgradeStatus.Upgrading">{{
                        $t('firmware_upgrade_tips') }}</div>
                    <div class="progress-text" v-if="upgradeStatus == FirmwareUpgradeStatus.BootToApp">{{
                        $t('upgrade_complete') }}</div>
                </div>
                <div class="progress-message">{{ $t('firmware_upgrade_progress') }}</div>
            </div>
        </div>
        <div class="progress-bar" v-if="isUpgrading">
            <div class="progress" :style="{ width: progress + '%' }"></div>
        </div>
    </div>

</template>

<script lang="ts">
const isShowUpgradeFirmware = ref(false);
export { isShowUpgradeFirmware };
</script>

<script setup lang="ts">
import { DriverService } from '@/service/driver/driver';
import { DriverSocketMsg, DriverSocketMsgType } from '@libs/types/message';
import { DialogType, enableDialog } from '../cdialog/dialog';
import { onMounted, ref } from 'vue';
import i18n from '@/languages/i18n';
import { FirmwareUpdateMessage } from '@libs/types/response';
import { FirmwareUpgradeStatus } from '@libs/types/enum';
let { t } = i18n.global
const filePath = ref<string>('');
let arrayBuffer: ArrayBuffer | null = null;
const upgradeStatus = ref<FirmwareUpgradeStatus>(FirmwareUpgradeStatus.None);
const isUpgrading = ref(false);
const progress = ref(0);
const handleFileUpload = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
        if (!file.name.endsWith('.bin')) {
            enableDialog(t("select_file_first"), DialogType.notice, false);
            return
        }
        filePath.value = file.name;
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = () => {
            arrayBuffer = reader.result as ArrayBuffer;
        }
    }
}
const handleUpgrade = () => {
    if (isUpgrading.value) {
        return
    }
    if (arrayBuffer == null) {
        isUpgrading.value = false;
        enableDialog(t("upgrade_fail"), DialogType.notice, false);
        return
    }
    const bytes = new Uint8Array(arrayBuffer);
    const data = {
        Type: DriverSocketMsgType.MTDeviceUpdate,
        Data: Array.from(bytes)
    } as DriverSocketMsg<any>;
    isUpgrading.value = true;
    DriverService.instance.askTypeAndData<boolean>(data).then((res: boolean) => {
        if (res) {
            enableDialog(t("upgrade_success"), DialogType.notice, true);
            back();
        } else {
            isUpgrading.value = false;
            arrayBuffer = null;
            filePath.value = "";
            enableDialog(t("upgrade_fail"), DialogType.notice, false);
        }
    })
}
const back = () => {
    isUpgrading.value = false;
    isShowUpgradeFirmware.value = false;
    arrayBuffer = null;
    filePath.value = "";
    progress.value = 0;
}
onMounted(() => {
    DriverService.instance.setHandler(DriverSocketMsgType.MSDeviceUpdateProgress, (msg: FirmwareUpdateMessage) => {
        upgradeStatus.value = msg.Status;
        if (msg.Status == FirmwareUpgradeStatus.Upgrading) {
            progress.value = msg.Current / msg.Total * 100;
        }
    })
})
</script>

<style scoped lang="less">
.firmware-upgrade {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    // background-image: url(/src/assets/img/background.png), linear-gradient(125deg, #ff7a00 0%, #ffc201 100%);
    background-size: auto 100%;
    z-index: 2;

    .upgrade-view {
        width: 892px;
        min-height: 180px;
    }

    .select-file {
        display: flex;
        height: 58px;

        .file-name {
            flex: 1;
            min-width: 558px;
            border-radius: var(--action-btn-radius);
            background-color: rgba(#FFFFFF, 0.4);
            padding: 16px 20px;
            color: rgba(#FFFFFF, 0.8);
            font-size: 20px;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }

        .select-file-btn {
            display: grid;
            margin-left: 50px;
            min-width: 214px;

            .file-input {
                grid-area: 1/1;
                height: 100%;
                width: 214px;
                opacity: 0;
                cursor: pointer;
                z-index: 2;
            }

            .mgx_action_btn {
                grid-area: 1/1;
                height: 58px;
                width: 100%;
            }
        }

    }

    .upgrade-message {
        margin-top: 24px;
        color: rgba(#FFFFFF, 0.8);
        font-size: 20px;
        width: 100%;
    }

    .start-upgrade {
        margin-top: 24px;
        height: 58px;
        display: flex;
        width: 100%;
        align-items: center;

        .start_upgrade_text {
            min-width: 558px;
            flex: 1;
            color: rgba(#FFFFFF, 0.8);
            font-size: 20px;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }

        .mgx_action_btn {
            height: 58px;
            min-width: 214px;
            margin-left: 50px;
        }
    }

    .mgx_action_btn {
        background: var(--glass-ghost-bg);
        backdrop-filter: var(--glass-ghost-blur);
        border: 1px solid var(--glass-ghost-border);
        color: var(--action-btn-ghost-text);
        height: 42px;
        padding: 0 24px;
        border-radius: var(--action-btn-radius);
        font-size: 14px;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        cursor: pointer;
        transition: var(--transition-base);

        .btn_icon {
            width: 18px;
            height: 18px;
            stroke-width: 2.5;
            transition: var(--transition-base);
        }

        &:hover {
            transform: translateY(-2px);
            background: var(--glass-ghost-hover-bg);
            
            .btn_icon {
                transform: scale(1.1);
            }
        }
    }

    .upgrade-progress {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .progress-text-container {
            display: flex;
            color: #FFFFFF;
            align-items: center;

            .icon {
                width: 28px;
                height: 28px;
                margin-right: 16px;

            }

            .progress-text {
                font-size: 24px;
                font-weight: bold;
            }
        }

        .progress-message {
            margin-top: 60px;
            color: rgba(#FFFFFF, 0.8);
            font-size: 20px;
        }
    }
}

.progress-bar {
    position: fixed;
    bottom: 30px;
    left: 0;
    right: 0;
    width: 100%;
    background-color: #FFFFFF;
    height: 8px;
    border: #FFB535 1px solid;

    .progress {
        position: absolute;
        top: 1px;
        bottom: 1px;
        left: 0;
        background: linear-gradient(90deg, #FFC201, #FF7A00);
    }
}
</style>
